import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, ArrowLeft } from 'lucide-react';
import { ApiError } from '@/lib/api';
import { useAdminGameConfig, useUpsertGameConfig, useDeleteGameConfig } from '../hooks';

// Client-side UX hint mirroring the server cap DFE_GAME_CONFIG_DATA_MAX_BYTES
// (256 KB) in @fias/db-types. The server is authoritative — it re-validates on
// PUT and returns 400 over-limit — so this only drives the live byte counter
// and the disabled Save state. We intentionally do NOT import the constant
// from @fias/db-types: that package is Node-oriented (Buffer/uuid) and is not a
// webapp dependency. If the server cap changes, bump this to match.
const MAX_BYTES = 256 * 1024;

function prettyPrint(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return '';
  }
}

export function ConfigDetailPage() {
  const { keyword = '' } = useParams<{ keyword: string }>();
  const navigate = useNavigate();
  const { config, notFound, loading, error } = useAdminGameConfig(keyword);
  const upsert = useUpsertGameConfig();
  const del = useDeleteGameConfig();

  const [text, setText] = useState('');
  const [description, setDescription] = useState('');
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // Seed the editor once the row loads (or to an empty object for create mode).
  useEffect(() => {
    if (config) {
      setText(prettyPrint(config.data));
      setDescription(config.description ?? '');
    } else if (notFound) {
      setText('{\n  \n}');
      setDescription('');
    }
  }, [config, notFound]);

  const byteLength = useMemo(() => new Blob([text]).size, [text]);
  const overSize = byteLength > MAX_BYTES;
  const isCreate = notFound && !config;

  const handleSave = async () => {
    setSaved(false);
    let parsed: unknown;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }
    setJsonError(null);
    await upsert.mutateAsync({
      keyword,
      data: parsed,
      description: description.trim() || null,
    });
    setSaved(true);
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete config "${keyword}"? This cannot be undone.`)) return;
    await del.mutateAsync(keyword);
    navigate('/a/arche_dfe_game_state/admin/config');
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div>
        <Link
          to="/a/arche_dfe_game_state/admin/config"
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-2"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to Config
        </Link>
      </div>

      {loading ? (
        <Skeleton className="h-64" />
      ) : error ? (
        <Card className="border-destructive">
          <CardContent className="py-4 flex items-center gap-2">
            <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="font-mono text-lg">{keyword}</CardTitle>
              <Badge variant={isCreate ? 'secondary' : 'outline'}>
                {isCreate ? 'new' : 'editing'}
              </Badge>
            </div>
            {config && (
              <p className="text-sm text-muted-foreground">
                Updated {new Date(config.updatedAt).toLocaleString()} by {config.updatedBy}
              </p>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label
                htmlFor="config-description"
                className="text-xs text-muted-foreground block mb-1"
              >
                Description (optional)
              </label>
              <input
                id="config-description"
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What this config controls…"
                className="w-full px-3 py-2 border rounded-md bg-background text-sm"
              />
            </div>

            <div>
              <label htmlFor="config-json" className="text-xs text-muted-foreground block mb-1">
                Data (JSON)
              </label>
              <Textarea
                id="config-json"
                value={text}
                onChange={(e) => {
                  setText(e.target.value);
                  setSaved(false);
                }}
                className="font-mono text-sm min-h-[320px]"
                placeholder='{ "tiles": [ { "archeId": "arche_cube", "name": "Cube", "image": "dfe-icons/litcraft-front-page/cube.png" } ] }'
              />
              <div className="flex items-center justify-between mt-1">
                <span
                  className={`text-xs ${overSize ? 'text-destructive' : 'text-muted-foreground'}`}
                >
                  {byteLength.toLocaleString()} / {MAX_BYTES.toLocaleString()} bytes
                </span>
                {saved && !upsert.isPending && (
                  <span className="text-xs text-green-600">Saved</span>
                )}
              </div>
            </div>

            {jsonError && <p className="text-sm text-destructive">Invalid JSON: {jsonError}</p>}
            {upsert.isError && (
              <div className="text-sm text-destructive space-y-1">
                <p>{upsert.error instanceof Error ? upsert.error.message : 'Save failed'}</p>
                {upsert.error instanceof ApiError && upsert.error.issues && (
                  <ul className="list-disc pl-5 space-y-0.5">
                    {upsert.error.issues.map((issue, i) => (
                      <li key={`${issue.path}-${i}`}>
                        {issue.path && <span className="font-mono">{issue.path}</span>}
                        {issue.path && ' — '}
                        {issue.message}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <div className="flex items-center justify-between">
              {!isCreate ? (
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  disabled={del.isPending || upsert.isPending}
                >
                  {del.isPending ? 'Deleting…' : 'Delete'}
                </Button>
              ) : (
                <span />
              )}
              <Button onClick={handleSave} disabled={upsert.isPending || overSize}>
                {upsert.isPending ? 'Saving…' : isCreate ? 'Create' : 'Save'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
